import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { compose, filter, isEmpty, prop, sort } from 'ramda';
import { isNilOrEmptyString, isObject } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import AutocompleteCustomOptionKey from 'components/atoms/GefMui/AutocompleteCustomOptionKey';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { EMPTY_VALUES } from 'constants/general';
import {
	changeQuickFilter,
	resetCriteria,
	resetToInitialState,
	setInitialFormValues,
	submitForm,
} from 'features/views/contactHistory/actions';
import { CONTACT_HISTORY_FORM_NAME, FIELDS } from 'features/views/contactHistory/form';
import { getExactlyXYearsAgoAsDateObj } from 'utils/apiDateTime';

export const Filter = ({
	productGroups,
	productGroupRel,
	productTypes,
	communicationActivities,
	communicationChannel,
}) => {
	const dispatch = useDispatch();
	const next = useControlNext(CONTACT_HISTORY_FORM_NAME);
	const formData = useFormData();
	const formIsInitiated = isObject(formData);
	const serviceId = formData?.service;

	useEffect(() => {
		if (formIsInitiated) {
			dispatch(setInitialFormValues());
			dispatch(submitForm());
		}
	}, [dispatch, formIsInitiated]);

	useEffect(
		() => () => {
			dispatch(resetToInitialState());
		},
		[dispatch],
	);

	const optionsForProduct = useMemo(() => {
		const sortAlphabetic = (a, b) => a.name.toString().localeCompare(b.name);

		const productGroupsLoaded = !isEmpty(productGroups);
		const productGroupRelLoaded = !isEmpty(productGroupRel);
		const productTypesLoaded = !isEmpty(productTypes);

		if (productGroupsLoaded && productGroupRelLoaded && productTypesLoaded) {
			// dividing the originalId into groupId and product cleanId
			const fixedProductGroupRel = productGroupRel.map((pgr) => {
				const splittedId = pgr.id.split('-', 2);
				return {
					...pgr,
					groupId: splittedId[0],
					cleanId: splittedId[1],
				};
			});

			const productGroupType3 = productGroups.filter((productGroup) => productGroup.groupType === 3);

			const productGroupType3Ids = productGroupType3.map((product) => product.id);

			const filteredProductGroupRel = fixedProductGroupRel.filter((product) =>
				productGroupType3Ids.includes(product.groupId),
			);

			const finalProductsList = filteredProductGroupRel.map((p) => {
				const productFromProductTypes = productTypes.find((pt) => pt.id === p.cleanId);

				return {
					code: p.cleanId,
					name: productFromProductTypes.shortDescription,
				};
			});

			return sort(sortAlphabetic)(finalProductsList);
		} else {
			return EMPTY_VALUES.ARRAY;
		}
	}, [productGroups, productGroupRel, productTypes]);

	const optionsForService = useMemo(
		() =>
			compose(
				filter((option) => option.read === 1),
				filter(compose(isNilOrEmptyString, prop('parentId'))),
			)(communicationActivities),
		[communicationActivities],
	);

	const optionsForServiceType = useMemo(
		() =>
			compose(
				filter((option) => option.read === 1),
				filter((service) => service.parentId === Number(serviceId) && service.result === 0),
			)(communicationActivities),
		[communicationActivities, serviceId],
	);

	const optionsForContactType = useMemo(
		() => filter((item) => prop('write', item) === 1)(communicationChannel),
		[communicationChannel],
	);

	const handleServiceChange = useCallback(() => {
		next(setValue({ fieldName: FIELDS.serviceType, value: null }));
	}, [next]);

	const handleResetFormCriteria = useCallback(() => {
		dispatch(resetCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(changeQuickFilter(null));
		dispatch(submitForm());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('contactHistory:policyNo')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.product}
							label={getLabelMessage('contactHistory:product')}
							options={optionsForProduct}
							labelPropName="name"
							valuePropName="code"
						/>
					</Grid>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.service}
							label={getLabelMessage('contactHistory:service')}
							options={optionsForService}
							labelPropName="shortDescription"
							onChange={handleServiceChange}
						/>
					</Grid>
					<Grid size={3}>
						<AutocompleteCustomOptionKey
							size="small"
							fieldName={FIELDS.serviceType}
							label={getLabelMessage('contactHistory:serviceType')}
							options={optionsForServiceType}
							labelPropName="shortDescription"
							keyPropName="id"
							disabled={isNilOrEmptyString(serviceId)}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.contactType}
							label={getLabelMessage('contactHistory:contactType')}
							options={optionsForContactType}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.dateFrom}
							label={getLabelMessage('contactHistory:dateFrom')}
							minDate={getExactlyXYearsAgoAsDateObj(-1)}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.dateTo}
							label={getLabelMessage('contactHistory:dateTo')}
							minDate={getExactlyXYearsAgoAsDateObj(-1)}
						/>
					</Grid>
					<Grid size={4} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleResetFormCriteria} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

Filter.propTypes = {
	communicationActivities: PropTypes.array,
	communicationActivityResults: PropTypes.array,
	communicationChannel: PropTypes.array,
	productGroupRel: PropTypes.array,
	productGroups: PropTypes.array,
	productTypes: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.productGroup, 'productGroups'),
	codelistProvider(codelists.productGroupRel, 'productGroupRel'),
	codelistProvider(codelists.productTypes, 'productTypes'),
	codelistProvider(codelists.communicationActivity, 'communicationActivities'),
	codelistProvider(codelists.communicationActivityResult, 'communicationActivityResults'),
	codelistProvider(codelists.communicationChannel, 'communicationChannel'),
)(Filter);
